import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDownIcon, DownloadIcon } from 'lucide-react';
import { Button } from './ui/Button';
export function Hero() {
  const handleScrollToImpact = () => {
    document.querySelector('#impact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleScrollToBioKit = () => {
    document.querySelector('#bio-kit')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section id="vision" className="relative min-h-screen flex items-center bg-[#1C1F23] overflow-hidden" aria-label="Hero — Vision" data-id="element-413">
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent 60px,
            #B08D57 60px,
            #B08D57 61px
          ), repeating-linear-gradient(
            90deg,
            transparent,
            transparent 60px,
            #B08D57 60px,
            #B08D57 61px
          )`
    }} aria-hidden="true" data-id="element-414" />

      {/* Gold accent line — left */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-[#B08D57] to-transparent opacity-60" aria-hidden="true" data-id="element-415" />

      <div className="relative max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40" data-id="element-416">
        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24" data-id="element-417">
          {/* Left — text content */}
          <div className="flex-1 max-w-3xl" data-id="element-418">
            {/* Eyebrow */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} data-id="element-419">
              <span className="inline-flex items-center gap-2 text-[#B08D57] text-sm font-body font-semibold tracking-[0.2em] uppercase mb-6" data-id="element-420">
                <span className="w-8 h-px bg-[#B08D57]" aria-hidden="true" data-id="element-421" />
                Senior Director · Product, Experience & Brand Systems
              </span>
            </motion.div>

            {/* Main headline */}
            <motion.h1 className="font-heading font-bold text-[#F7F7F5] leading-tight mb-6" style={{
            fontSize: 'clamp(2.5rem, 6vw, 5rem)'
          }} initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: 'easeOut'
          }} data-id="element-422">
              Michael K. <span className="text-[#B08D57]" data-id="element-423">Bigos</span>
            </motion.h1>

            {/* Divider */}
            <motion.div className="w-16 h-0.5 bg-[#B08D57] mb-8" initial={{
            scaleX: 0,
            originX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} aria-hidden="true" data-id="element-424" />

            {/* Subhead */}
            <motion.div className="font-body font-light text-[#C4D4DC] leading-relaxed mb-12 max-w-2xl" style={{
            fontSize: 'clamp(1rem, 2vw, 1.25rem)'
          }} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.3,
            ease: 'easeOut'
          }} data-id="element-425">
              <p className="text-[#B08D57] font-semibold text-sm tracking-widest uppercase mb-3" data-id="element-426">
                Senior Product & Brand Designer | Thought Leader
              </p>
              <p data-id="element-427">
                Architecting scalable enterprise systems and design governance
                frameworks that harmonize AI-integrated modernization with
                omni-channel brand ecosystems. I help organizations drive
                measurable growth, foster cultural alignment, and deliver
                consistently elevated customer experiences through strategic
                design leadership and innovation.
              </p>
            </motion.div>

            {/* CTAs */}
            <motion.div className="flex flex-col sm:flex-row gap-4" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.5
          }} data-id="element-428">
              <Button variant="primary" size="lg" onClick={handleScrollToImpact} rightIcon={<ArrowDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-430" />} aria-label="View Enterprise Impact — scroll to impact section" data-id="element-429">
                View Enterprise Impact
              </Button>
              <Button variant="outline" size="lg" onClick={() => document.querySelector('#case-studies')?.scrollIntoView({
              behavior: 'smooth'
            })} leftIcon={<ArrowDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-432" />} className="border-[#F7F7F5]/30 text-[#F7F7F5] hover:bg-[#F7F7F5] hover:text-[#1C1F23]" aria-label="View Case Studies and Portfolio" data-id="element-431">
                View Case Studies & Portfolio
              </Button>
            </motion.div>

            {/* Credential strip */}
            <motion.div className="mt-16 pt-8 border-t border-[#F7F7F5]/10 flex flex-wrap gap-6 text-[#8FA4B0] text-xs font-body tracking-widest uppercase" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 0.8
          }} data-id="element-433">
              <span data-id="element-434">Section 508 Compliant</span>
              <span aria-hidden="true" data-id="element-435">·</span>
              <span data-id="element-436">WCAG AA</span>
              <span aria-hidden="true" data-id="element-437">·</span>
              <span data-id="element-438">Federal & Enterprise</span>
              <span aria-hidden="true" data-id="element-439">·</span>
              <span data-id="element-440">AI/GAI Integration</span>
              <span aria-hidden="true" data-id="element-441">·</span>
              <span data-id="element-442">Adobe CC</span>
              <span aria-hidden="true" data-id="element-443">·</span>
              <span data-id="element-444">Figma</span>
              <span aria-hidden="true" data-id="element-445">·</span>
              <span data-id="element-446">Jira</span>
              <span aria-hidden="true" data-id="element-447">·</span>
              <span data-id="element-448">Asana</span>
              <span aria-hidden="true" data-id="element-449">·</span>
              <span data-id="element-450">Wrike</span>
              <span aria-hidden="true" data-id="element-451">·</span>
              <span data-id="element-452">Azure</span>
              <span aria-hidden="true" data-id="element-453">·</span>
              <span data-id="element-454">ML/AI</span>
              <span aria-hidden="true" data-id="element-455">·</span>
              <span data-id="element-456">Android</span>
              <span aria-hidden="true" data-id="element-457">·</span>
              <span data-id="element-458">iOS</span>
              <span aria-hidden="true" data-id="element-459">·</span>
              <span data-id="element-460">Mobile</span>
              <span aria-hidden="true" data-id="element-461">·</span>
              <span data-id="element-462">B2B</span>
              <span aria-hidden="true" data-id="element-463">·</span>
              <span data-id="element-464">B2C</span>
              <span aria-hidden="true" data-id="element-465">·</span>
              <span data-id="element-466">SaaS</span>
            </motion.div>
          </div>

          {/* Right — portrait */}
          <motion.div className="flex-shrink-0 flex items-center justify-center order-first lg:order-last" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.3,
          ease: 'easeOut'
        }} data-id="element-467">
            <div className="relative" aria-hidden="true" data-id="element-468">
              {/* Outer decorative ring — slow rotation */}
              <motion.div className="absolute inset-0 rounded-full border border-dashed border-[#B08D57]/30" style={{
              margin: '-16px'
            }} animate={{
              rotate: 360
            }} transition={{
              duration: 30,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-469" />

              {/* Gold glow */}
              <div className="absolute inset-0 rounded-full" style={{
              boxShadow: '0 0 40px 8px rgba(176, 141, 87, 0.18)',
              margin: '-4px'
            }} data-id="element-470" />

              {/* Gold ring border */}
              <div className="w-[500px] h-[500px] rounded-full p-[3px] bg-gradient-to-br from-[#B08D57] via-[#D4AA6E] to-[#8E6F3E]" data-id="element-471">
                <div className="w-full h-full rounded-full overflow-hidden bg-[#2F3A45]" data-id="element-472">
                  <img src="https://cdn.magicpatterns.com/uploads/1brQDH1c16biSFztn2b1or/IMG_0914.jpg" alt="Michael K. Bigos Jr. — Senior Director, Product, Experience & Brand Systems" className="w-full h-full object-cover object-top" data-id="element-473" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2" animate={{
      y: [0, 8, 0]
    }} transition={{
      repeat: Infinity,
      duration: 2,
      ease: 'easeInOut'
    }} aria-hidden="true" data-id="element-474">
        <ArrowDownIcon className="w-5 h-5 text-[#B08D57]/50" data-id="element-475" />
      </motion.div>
    </section>;
}